import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatNativeDateModule } from '@angular/material/core';
import {
  MatDatepicker,
  MatDatepickerInput,
  MatDatepickerToggle,
} from '@angular/material/datepicker';
import {
  MatError,
  MatFormField,
  MatLabel,
  MatSuffix,
} from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import * as moment from 'moment';
import { NgxPaginationModule } from 'ngx-pagination';
import { NgxSpinnerService } from 'ngx-spinner';
import { ApiServiceService } from '../api-service.service';
import { CommonPaginationComponent } from '../common-component-pagination/common-component-pagination.component';
import { CommonSearchComponent } from '../common-component-search/common-component-search.component';
import { SharedValidators } from '../shared/validators/shared-validators';

@Component({
  selector: 'app-dispatch-device-list',
  templateUrl: './dispatch-device-list.component.html',
  styleUrls: ['./dispatch-device-list.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatIcon,
    NgxPaginationModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatSelect,
    MatOption,
    MatError,
    MatTooltip,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    MatNativeDateModule,
    CommonSearchComponent,
    CommonPaginationComponent,
  ],
})
export class DispatchDeviceListComponent implements OnInit {
  size = 10;
  page = 1;
  searchText = '';
  totalLength = 0;
  dispatchDeviceList = [];
  // Device Models (from the Device Model master) used for the TCU Model dropdown
  deviceModelList: { _id: string; modelName: string }[] = [];

  userPermission = [];

  dispatchDeviceForm: FormGroup = this.formBuilder.group({});
  addNewDevice = false;

  dispatchDeviceUpdate = false;
  dispatchDeviceDelete = false;
  dispatchDeviceCreate = false;
  dispatchDeviceView = false;

  selectedDispatchDevice;
  selectedDeviceId = '';
  selectedDispatchDeviceId = '';
  isDownloading = false;
  // storage listener for cross-tab sync
  private storageListener = (event: StorageEvent) => {
    if (event.key === 'downloadInProgress') {
      this.isDownloading = event.newValue === 'true';
    }
  };

  constructor(
    public apiMicroservice: ApiServiceService,
    private formBuilder: FormBuilder,
    public router: Router,
    private spinner: NgxSpinnerService
  ) {}

  ngOnInit(): void {
    this.setDispatchDeviceForm();
    this.checkPermission();
    this.syncInitialDownloadState();
    if (this.dispatchDeviceView) {
      this.fetchDispatchDeviceList();
    }
    this.fetchDeviceModelList();
  }

  // Loads all Device Models for the TCU Model dropdown. The model list API is
  // paginated, so a large page size is requested to get every model at once.
  fetchDeviceModelList() {
    this.apiMicroservice.getFirmwareList(1, 1000, '').subscribe({
      next: (res) => {
        if (res?.status) {
          this.deviceModelList = res.data?.data || [];
        } else {
          this.deviceModelList = [];
          this.apiMicroservice.openSnackbar(res?.message);
        }
      },
      error: (error) => {
        this.deviceModelList = [];
        const errorMessage =
          error?.error?.message || 'Failed to load device models';
        this.apiMicroservice.openSnackbar(errorMessage);
      },
    });
  }

  // True when the form's current TCU Model (e.g. from an existing device) is
  // not among the loaded Device Models, so it can still be shown/kept.
  isTCUModelMissingFromList(): boolean {
    const value = this.dispatchDeviceForm?.get('TCUModel')?.value;
    return (
      !!value && !this.deviceModelList.some((m) => m.modelName === value)
    );
  }

  checkPermission() {
    this.userPermission = JSON.parse(
      this.apiMicroservice.decryptData(
        sessionStorage.getItem('userPermission') || '[]'
      )
    );
    this.userPermission.forEach(
      (element: {
        pageName: string;
        view: boolean;
        create: boolean;
        delete: boolean;
        update: boolean;
      }) => {
        if (element.pageName === 'Dispatch Device' && element.view === false) {
          this.navigateBack();
        }
        if (element.pageName === 'Dealer Master' && element.view === true) {
          this.dispatchDeviceView = true;
        }
        if (element.pageName === 'Dispatch Device' && element.create === true) {
          this.dispatchDeviceCreate = true;
        }
        if (element.pageName === 'Dispatch Device' && element.update === true) {
          this.dispatchDeviceUpdate = true;
        }
        if (element.pageName === 'Dispatch Device' && element.delete === true) {
          this.dispatchDeviceDelete = true;
        }
      }
    );
  }

  syncInitialDownloadState() {
    // sync initial download state across tabs
    this.isDownloading = localStorage.getItem('downloadInProgress') === 'true';
    window.addEventListener('storage', this.storageListener);
  }

  setDispatchDeviceForm() {
    this.dispatchDeviceForm = this.formBuilder.group({
      dispatchDeviceSearch: new FormControl(''),
      _id: new FormControl('', SharedValidators.requiredAndNoEmptySpaces),
      dispatchDeviceId: new FormControl(''),
      UIN: new FormControl('', [
        SharedValidators.requiredAndNoEmptySpaces,
        SharedValidators.exactLengthValidator(19),
      ]),
      IMEI: new FormControl('', [
        SharedValidators.requiredAndNoEmptySpaces,
        SharedValidators.exactLengthValidator(15),
      ]),
      ICCID: new FormControl('', [
        SharedValidators.requiredAndNoEmptySpaces,
        SharedValidators.exactLengthValidator(20),
      ]),
      TMLPN: new FormControl('', SharedValidators.requiredAndNoEmptySpaces),
      TCUModel: new FormControl('', SharedValidators.requiredAndNoEmptySpaces),
      ProdDate: new FormControl('', [this.dateFormatValidator]),
      TCUTestStatus: new FormControl(
        '',
        SharedValidators.requiredAndNoEmptySpaces
      ),
      TCUFirmwareVersion: new FormControl(
        '',
        SharedValidators.requiredAndNoEmptySpaces
      ),
      BootstrapExpDate: new FormControl('', [this.dateFormatValidator]),
      SIMServiceOperator: new FormControl(
        '',
        SharedValidators.requiredAndNoEmptySpaces
      ),
      EmissionType: new FormControl(
        '',
        SharedValidators.requiredAndNoEmptySpaces
      ),
      SIMVendor: new FormControl('', SharedValidators.requiredAndNoEmptySpaces),
      tmlstatus: new FormControl('', SharedValidators.requiredAndNoEmptySpaces),
    });
  }

  // Custom date format validator to check "YYYY-MM-DD"
  dateFormatValidator(control: FormControl) {
    const isValid = moment(control.value, 'YYYY-MM-DD', true).isValid();
    return isValid ? null : { invalidDateFormat: true };
  }

  // Utility function to convert Excel date serial number to "YYYY-MM-DD" format
  convertExcelDate(serialStr: string): string {
    const serial = parseInt(serialStr, 10); // Convert string to number
    const baseDate = new Date(1900, 0, 1); // Base date is "1900-01-01"
    const correctedSerial = serial - 1; // Excel serial date starts from day 1, so subtract 1
    const calculatedDate = new Date(
      baseDate.getTime() + correctedSerial * 86400000
    ); // Add days in milliseconds
    return moment(calculatedDate).format('YYYY-MM-DD'); // Format to "YYYY-MM-DD"
  }

  formatToYYYYMMDD(date: string | Date): string {
    if (!date) return '';

    const d = new Date(date);
    if (isNaN(d.getTime())) {
      // Return the original value as a string if the date is invalid
      return String(date);
    }

    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0'); // Months are 0-based
    const day = String(d.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  fetchDispatchDeviceList() {
    this.spinner.show();
    this.searchText =
      this.dispatchDeviceForm.get('dispatchDeviceSearch')?.value || '';
    this.dispatchDeviceList = [];
    this.totalLength = 0;
    this.apiMicroservice
      .getDispatchDeviceList(this.page, this.size, this.searchText)
      .subscribe((res) => {
        if (res.status) {
          this.spinner.hide();
          this.dispatchDeviceList = res.data.data;
          this.totalLength = res.data.totalItems;
          this.apiMicroservice.openSnackbar(res.message);
        } else {
          this.spinner.hide();
          this.apiMicroservice.openSnackbar(res.message);
        }
      });
  }

  save() {
    if (this.dispatchDeviceForm.invalid) {
      SharedValidators.markAllAsTouched(this.dispatchDeviceForm);
    } else {
      this.updateDispatchedDevice();
    }
  }

  updateDispatchedDevice() {
    const payload = this.dispatchDeviceForm.value;
    this.apiMicroservice.updateDispatchDeviceData(payload).subscribe({
      next: (res) => {
        this.cancelFunc();
        if (res.status) {
          this.apiMicroservice.openSnackbar(res.message);
          this.resetForm();
          this.fetchDispatchDeviceList();
        } else {
          this.apiMicroservice.openSnackbar(res.message);
        }
      },
      error: (error) => {
        const errorMessage =
          error?.error.message || 'An unexpected error occurred';
        this.apiMicroservice.openSnackbar(errorMessage);
      },
    });
  }

  prePopulation1(dispatchDevice) {
    this.selectedDeviceId = dispatchDevice.deviceId;
    this.selectedDispatchDeviceId = dispatchDevice.dispatchDeviceId;
    this.selectedDispatchDevice = dispatchDevice;
    this.prePopulation(this.selectedDispatchDevice);
  }

  prePopulation(selectedDispatchDevice) {
    if (selectedDispatchDevice.dispatchDeviceId) {
      this.spinner.show();
      this.addNewDevice = true;
      this.apiMicroservice
        .getDispatchDeviceById(selectedDispatchDevice.dispatchDeviceId)
        .subscribe((res) => {
          if (res.status) {
            this.spinner.hide();
            this.dispatchDeviceForm.patchValue(res.data[0]);
            this.dispatchDeviceForm
              ?.get('_id')
              ?.patchValue(this.selectedDeviceId);
            this.dispatchDeviceForm
              ?.get('dispatchDeviceId')
              ?.patchValue(this.selectedDispatchDeviceId);
            this.dispatchDeviceForm
              ?.get('ProdDate')
              ?.patchValue(
                res.data[0].ProdDate && /^\d+$/.test(res.data[0].ProdDate)
                  ? this.convertExcelDate(res.data[0].ProdDate)
                  : res.data[0].ProdDate
                    ? this.formatToYYYYMMDD(String(res.data[0].ProdDate))
                    : res.data[0].ProdDate
              );
            this.dispatchDeviceForm
              ?.get('BootstrapExpDate')
              ?.patchValue(
                res.data[0].BootstrapExpDate &&
                  /^\d+$/.test(res.data[0].BootstrapExpDate)
                  ? this.convertExcelDate(res.data[0].BootstrapExpDate)
                  : res.data[0].BootstrapExpDate
                    ? this.formatToYYYYMMDD(
                        String(res.data[0].BootstrapExpDate)
                      )
                    : res.data[0].BootstrapExpDate
              );
          }
        });
    } else {
      this.setDispatchDeviceForm();
      this.dispatchDeviceForm?.get('_id')?.patchValue(this.selectedDeviceId);
      this.dispatchDeviceForm
        ?.get('UIN')
        ?.patchValue(this.selectedDispatchDevice.uid);
      this.dispatchDeviceForm
        ?.get('ICCID')
        ?.patchValue(this.selectedDispatchDevice.iccid);
      this.dispatchDeviceForm
        ?.get('IMEI')
        ?.patchValue(this.selectedDispatchDevice.imei);
      this.addNewDevice = true;
    }
  }

  addNewDeviceFunc() {
    this.router.navigate(['dispatch-device-add']);
  }

  // Cancel and hide add/edit form
  cancelFunc() {
    this.addNewDevice = false;
    this.resetForm();
  }

  onPageChange(event: number) {
    this.page = event;
    this.fetchDispatchDeviceList();
  }

  onSizeChange(event: number) {
    this.page = 1;
    this.size = event;
    this.fetchDispatchDeviceList();
  }

  onSearch(event: string) {
    this.page = 1;
    this.searchText = event;
    this.dispatchDeviceForm
      ?.get('dispatchDeviceSearch')
      ?.setValue(this.searchText ? this.searchText : '');
    this.fetchDispatchDeviceList();
  }

  // Reset the form and edit mode
  resetForm() {
    // Store the current value of the search field before resetting
    const searchValue =
      this.dispatchDeviceForm.get('dispatchDeviceSearch')?.value || '';

    // Reset the form with initial values, and set the validators
    this.dispatchDeviceForm = this.formBuilder.group({
      dispatchDeviceSearch: new FormControl(searchValue), // Retain the search value
      _id: new FormControl('', SharedValidators.requiredAndNoEmptySpaces),
      dispatchDeviceId: new FormControl(''),
      UIN: new FormControl('', [
        SharedValidators.requiredAndNoEmptySpaces,
        SharedValidators.exactLengthValidator(19),
      ]),
      IMEI: new FormControl('', [
        SharedValidators.requiredAndNoEmptySpaces,
        SharedValidators.exactLengthValidator(15),
      ]),
      ICCID: new FormControl('', [
        SharedValidators.requiredAndNoEmptySpaces,
        SharedValidators.exactLengthValidator(20),
      ]),
      TMLPN: new FormControl('', SharedValidators.requiredAndNoEmptySpaces),
      TCUModel: new FormControl('', SharedValidators.requiredAndNoEmptySpaces),
      ProdDate: new FormControl('', [this.dateFormatValidator]),
      TCUTestStatus: new FormControl(
        '',
        SharedValidators.requiredAndNoEmptySpaces
      ),
      TCUFirmwareVersion: new FormControl(
        '',
        SharedValidators.requiredAndNoEmptySpaces
      ),
      BootstrapExpDate: new FormControl('', [this.dateFormatValidator]),
      SIMServiceOperator: new FormControl(
        '',
        SharedValidators.requiredAndNoEmptySpaces
      ),
      EmissionType: new FormControl(
        '',
        SharedValidators.requiredAndNoEmptySpaces
      ),
      SIMVendor: new FormControl('', SharedValidators.requiredAndNoEmptySpaces),
      tmlstatus: new FormControl('', SharedValidators.requiredAndNoEmptySpaces),
    });
  }

  // Delete dealer
  deleteDispatchedDevice(dispatchedDevice) {
    if (confirm('Are you sure?')) {
      this.spinner.show();
      this.apiMicroservice
        .deleteDispatchDeviceData(
          dispatchedDevice.deviceId,
          dispatchedDevice.dispatchDeviceId
        )
        .subscribe((res) => {
          if (res.status) {
            this.fetchDispatchDeviceList();
            this.spinner.hide();
          }
        });
    }
  }

  // exportToCsvData() {
  //   if (confirm('Are you sure?')) {
  //     this.searchText = this.dispatchDeviceForm.get(
  //       'dispatchDeviceSearch'
  //     )?.value;
  //     this.spinner.show();
  //     this.apiMicroservice
  //       .getDispatchDeviceList(this.page, this.totalLength, this.searchText)
  //       .subscribe((res) => {
  //         if (res.status) {
  //           const exportData = res.data.data.map((item) =>
  //             omit(item, ['_id', 'deviceId', 'dispatchDeviceId'])
  //           );
  //           exportAsCsvFile(exportData, 'Dispatch_Device_Details_');
  //           this.apiMicroservice.openSnackbar('File Ready For Download.');
  //           this.spinner.hide();
  //         }
  //       });
  //   }
  // }

  downloadDispatchDeviceReport() {
    if (confirm('Are you sure?')) {
      // mark download in-progress and notify other tabs
      this.isDownloading = true;
      localStorage.setItem('downloadInProgress', 'true');

      this.apiMicroservice.openSnackbar(
        'Your file is being generated. Refreshing the page or logging out may interrupt the download.'
      );
      this.searchText = this.dispatchDeviceForm?.get(
        'dispatchDeviceSearch'
      )?.value;
      this.apiMicroservice
        .downloadDispatchDeviceReport(this.searchText)
        .subscribe({
          next: (res) => {
            try {
              if (res?.status && res.fileDownloadUrl) {
                const link = document.createElement('a');
                link.href = res.fileDownloadUrl;
                link.download = ''; // let browser infer filename
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                this.apiMicroservice.openSnackbar('File ready for download');
              } else {
                this.apiMicroservice.openSnackbar(
                  res?.message || 'File URL not available'
                );
              }
            } finally {
              // clear in-progress state
              this.isDownloading = false;
              localStorage.setItem('downloadInProgress', 'false');
            }
          },
          error: (ERROR: HttpErrorResponse) => {
            const example =
              ERROR.error.message ||
              'Download failed. Please contact server team.';
            this.apiMicroservice.openSnackbar(example);
            // clear in-progress state
            this.isDownloading = false;
            localStorage.setItem('downloadInProgress', 'false');
          },
        });
    }
  }

  navigateBack() {
    this.apiMicroservice.back();
  }
}
